import React, { useEffect, useMemo, useRef, useState } from "react";

function safeName(t) {
    return (t?.name || "Unknown").toString();
}

export default function VideoNFTPlayerComponent({
                                                    viewAddress,
                                                    isLoading,
                                                    isRefreshing,
                                                    tokens, // expects: video-capable tokens already (mediaType=video, has animationUrl)
                                                }) {
    // Player state lives HERE now (Option A)
    const videoRef = useRef(null);
    const [videoSearch, setVideoSearch] = useState("");
    const [videoActiveIndex, setVideoActiveIndex] = useState(0);
    const [videoShuffle, setVideoShuffle] = useState(false);
    const [videoRepeatOne, setVideoRepeatOne] = useState(false);

    const filteredList = useMemo(() => {
        const q = videoSearch.trim().toLowerCase();
        const list = (tokens || [])
            .filter((t) => !!t?.animationUrl)
            .filter((t) => (q ? safeName(t).toLowerCase().includes(q) : true))
            .slice()
            .sort((a, b) => safeName(a).localeCompare(safeName(b)));

        return list;
    }, [tokens, videoSearch]);

    const videoCurrent = filteredList[videoActiveIndex] || null;

    useEffect(() => {
        if (!filteredList.length) {
            setVideoActiveIndex(0);
            return;
        }
        if (videoActiveIndex > filteredList.length - 1) setVideoActiveIndex(0);
    }, [filteredList, videoActiveIndex]);

    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        if (!videoCurrent?.animationUrl) return;
        el.load();
        el.play().catch(() => {});
    }, [videoCurrent?.animationUrl]);

    const playIndex = (idx) => {
        if (!filteredList.length) return;
        const next = Math.max(0, Math.min(idx, filteredList.length - 1));
        setVideoActiveIndex(next);
    };

    const nextTrack = () => {
        if (!filteredList.length) return;
        if (videoShuffle) {
            const r = Math.floor(Math.random() * filteredList.length);
            setVideoActiveIndex(r);
            return;
        }
        setVideoActiveIndex((i) => (i + 1) % filteredList.length);
    };

    const prevTrack = () => {
        if (!filteredList.length) return;
        setVideoActiveIndex((i) => (i - 1 + filteredList.length) % filteredList.length);
    };

    const togglePlay = async () => {
        const el = videoRef.current;
        if (!el) return;
        if (el.paused) await el.play();
        else el.pause();
    };

    const onEnded = () => {
        if (videoRepeatOne) {
            const el = videoRef.current;
            if (el) {
                el.currentTime = 0;
                el.play().catch(() => {});
            }
            return;
        }
        nextTrack();
    };

    return (
        <div>
            <p className="audio-player-subtitle">
                Viewing wallet: <strong className="mono">{viewAddress}</strong>
            </p>

            {(isLoading || isRefreshing) && (
                <div className="loading-indicator">
          <span>
            Loading Wallet Contents. This may take a few minutes to complete. This message will disappear when finished.
          </span>
                    <div className="loading-spinner" />
                </div>
            )}

            <div className="video-player-layout">
                <main className="video-player-nowplaying">
                    {videoCurrent ? (
                        <div className="video-nowplaying-card">
                            <div className="video-nowplaying-media">
                                <video
                                    ref={videoRef}
                                    className="video-player-element"
                                    controls
                                    onEnded={onEnded}
                                    src={videoCurrent.animationUrl}
                                    poster={
                                        videoCurrent.image && videoCurrent.image !== "No image URL"
                                            ? videoCurrent.image
                                            : undefined
                                    }
                                />
                            </div>

                            <div className="audio-nowplaying-info">
                                <h2 className="audio-nowplaying-title">{safeName(videoCurrent)}</h2>
                                <p className="audio-nowplaying-title">{videoCurrent.description}</p>
                                <br />
                                <p className="audio-nowplaying-title">{videoCurrent.external_url}</p>
                                <br />

                                <div className="audio-nowplaying-links">
                                    <a href={`https://solscan.io/token/${videoCurrent.mint}`} target="_blank" rel="noreferrer">
                                        View Mint on Solscan
                                    </a>

                                    {videoCurrent.collectionKey && (
                                        <a
                                            href={`https://solscan.io/token/${videoCurrent.collectionKey}`}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            View Collection on Solscan
                                        </a>
                                    )}

                                    {videoCurrent.uri && videoCurrent.uri !== "No Metadata URI" && (
                                        <a href={videoCurrent.uri} target="_blank" rel="noreferrer">
                                            Open Metadata JSON
                                        </a>
                                    )}
                                </div>

                                <div className="audio-player-controls">
                                    <button onClick={prevTrack} type="button">⏮ Prev</button>
                                    <button onClick={togglePlay} type="button">⏯ Play/Pause</button>
                                    <button onClick={nextTrack} type="button">Next ⏭</button>

                                    <button
                                        type="button"
                                        className={videoShuffle ? "active-toggle" : ""}
                                        onClick={() => setVideoShuffle((v) => !v)}
                                        title="Shuffle"
                                    >
                                        🔀 Shuffle
                                    </button>

                                    <button
                                        type="button"
                                        className={videoRepeatOne ? "active-toggle" : ""}
                                        onClick={() => setVideoRepeatOne((v) => !v)}
                                        title="Repeat current video"
                                    >
                                        🔁 Repeat 1
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="video-nowplaying-card">
                            <p>Select a video from the playlist.</p>
                        </div>
                    )}
                </main>

                <aside className="audio-player-playlist">
                    <div className="audio-player-search">
                        <label>Search by name</label>
                        <input
                            type="text"
                            value={videoSearch}
                            onChange={(e) => setVideoSearch(e.target.value)}
                            placeholder="Start typing…"
                        />
                    </div>

                    <div className="audio-player-playlist-meta">
                        <p>
                            Videos found: <strong>{filteredList.length}</strong>
                        </p>
                    </div>

                    <div className="audio-player-tracklist">
                        {filteredList.map((t, idx) => {
                            const isActive = idx === videoActiveIndex;
                            return (
                                <button
                                    key={t.mint || `${t.name}-${idx}`}
                                    className={`audio-track-row ${isActive ? "active" : ""}`}
                                    onClick={() => playIndex(idx)}
                                    type="button"
                                >
                                    <div className="audio-track-row-title">{safeName(t)}</div>
                                    <div className="audio-track-row-sub mono">
                                        {t.type} {t.collectionKey ? `• ${String(t.collectionKey).slice(0, 8)}…` : ""}
                                    </div>
                                </button>
                            );
                        })}

                        {!filteredList.length && !isLoading && !isRefreshing && (
                            <p style={{ opacity: 0.85 }}>
                                No video NFTs found for this wallet (or no animationUrl present in metadata).
                            </p>
                        )}
                    </div>
                </aside>
            </div>
        </div>
    );
}
